// Groomba documentation page (the public /guide).
// Single-column prose layout patterned exactly on the design's docs.html:
// section <h2> → subsection <h3> → optional DocsNavPath → paragraphs / lists /
// callouts / DefLists, with Slack figures placed inline as block elements
// between paragraphs. <hr/> separates major sections.

import React, { useEffect } from 'react';
import {
  Prose, ProseHeading, Callout,
  DocsNavPath, DefList, DefItem, DocsFooter,
  FieldOptionList, FieldOption,
} from './Content';
import { SkFigure, SkCompose } from '../slack/Slack';
import {
  SlackAppHome, SlackCreateTeamModal, SlackGroomIssuesModal,
  SlackVotingMessage, SlackPostVotingOptions,
  SlackAutoPollSettings, SlackBacklogSettings, SlackReminderSettings,
  SlackAveragingSettings, SlackPollDisplaySettings,
  SlackOwnershipSettings, SlackWorkspaceSettings,
  SlackPortalMessage, SlackWorkspaceListing,
  SlackFeedbackModal,
} from '../slack/compositions/GuideFigures';

// On initial mount, if the URL has a hash, re-trigger the scroll. The
// browser's native hash-anchor scroll fires before React renders the
// heading IDs, so without this the deep link silently misses. After
// mount, all subsequent hash clicks are handled natively (with
// `scroll-behavior: smooth` and `scroll-margin-top` from CSS).
function useScrollToInitialHash() {
  useEffect(() => {
    const id = decodeURIComponent((location.hash || '').replace(/^#/, ''));
    if (!id) return;
    const target = document.getElementById(id);
    if (target) target.scrollIntoView();
  }, []);
}

// Replace any inline `<code>/groomba …</code>` in prose with a SkCompose
// bubble so commands read as Slack input chrome rather than CLI text. Run
// after every render so post-mount nav doesn't leave bare <code> behind.
function useSlashCommandSwap() {
  useEffect(() => {
    const codes = document.querySelectorAll('.gr-prose code');
    codes.forEach((el) => {
      if (el.dataset.composeReplaced) return;
      if (el.closest('.slack-root')) return; // already inside Slack context
      const text = (el.textContent || '').trim();
      if (!text.startsWith('/groomba')) return;
      el.dataset.composeReplaced = '1';
      el.classList.add('doc-compose-host');
      // Build the bubble structure inline. Keeping it static (no nested
      // React root) makes server-rendering and prefers-reduced-motion
      // straightforward.
      el.innerHTML =
        '<span class="slack-root doc-compose doc-compose--inline">' +
          '<span class="sk-compose">' +
            '<span class="sk-compose-text"><span class="cmd"></span></span>' +
            '<span class="sk-compose-send" aria-hidden="true">' +
              '<span class="ico">' +
                '<svg viewBox="0 0 20 20" fill="currentColor"><path d="M2.5 3.5 17.5 10 2.5 16.5 2.5 11.5 11.5 10 2.5 8.5Z"/></svg>' +
              '</span>' +
            '</span>' +
          '</span>' +
        '</span>';
      el.querySelector('.cmd').textContent = text;
    });
  });
}

export default function Guide({ iconSrc }) {
  useScrollToInitialHash();
  useSlashCommandSwap();
  return (
    <article className="guide-article" id="top">
      <Prose>
        <h1 className="guide-title">Groomba Guide</h1>
        <p className="lede">
          Everything you need to run async story-point estimation from inside Slack.
          For quick help, type <code>/groomba help</code>. To reach us, email{' '}
          <a href="mailto:hello@groomba.ai">hello@groomba.ai</a> or use <code>/groomba feedback</code>.
        </p>

        {/* ═══ GETTING STARTED ═══════════════════════════════ */}
        <ProseHeading level={2} id="getting-started">Getting started</ProseHeading>

        <ProseHeading level={3} id="creating-a-team">Creating a team</ProseHeading>
        <DocsNavPath command="/groomba new-team" />
        <p>
          A Groomba team contains your team's estimation participants and settings for how you want
          to estimate Jira issues.
        </p>
        <p>In team settings, you can configure:</p>
        <ul>
          <li>The pointing scale you'll use</li>
          <li>Reminder/notification frequency</li>
          <li>Averaging method</li>
          <li>Backlog location in Jira</li>
          <li>Auto-polling</li>
        </ul>

        <SkFigure variant="modal"><SlackCreateTeamModal iconSrc={iconSrc} /></SkFigure>

        <p>
          The <strong>estimate field</strong> determines which Jira field Groomba writes story points to.
          You can choose from any numeric custom field in your Jira instance using the searchable dropdown.
        </p>

        <FieldOptionList legend="Common choices for the estimate field">
          <FieldOption name="Story point estimate" recommended>
            <p>
              A default field (locked) in Jira, so you cannot delete it. This is selected automatically
              when you enable the Backlog and Estimation features in a team-managed space.
            </p>
          </FieldOption>
          <FieldOption name="Story Points" badge="Legacy">
            <p>
              An older field, but you may work with Jira spaces (formerly "projects") that still use it.
              Available for Story and Epic issue types in company-managed spaces by default.
            </p>
          </FieldOption>
          <FieldOption name="Custom numeric field" badge="Custom">
            <p>
              If your organization uses a different numeric field for estimation (e.g. <em>Work Estimate</em>,
              <em> Dev Story Points</em>), select it from the dropdown. Useful when multiple teams estimate
              independently using different fields.
            </p>
          </FieldOption>
        </FieldOptionList>

        <Callout kind="warn" title="Make sure your estimate field is on the edit screen.">
          <p>
            Jira uses <a href="https://support.atlassian.com/jira-cloud-administration/docs/manage-issue-screens/">screens</a>{' '}
            to control which fields appear when creating, editing, or viewing an issue. A field can exist
            in your Jira instance but still not be editable if it isn't on the edit screen for that issue type.
          </p>
          <ul>
            <li>
              <strong>Company-managed spaces:</strong> Ask a Jira admin to{' '}
              <a href="https://support.atlassian.com/jira-cloud-administration/docs/add-a-custom-field-to-a-screen/">add your estimate field</a>{' '}
              to the edit screen for the relevant issue types.
            </li>
            <li>
              <strong>Team-managed spaces:</strong> Check your{' '}
              <a href="https://support.atlassian.com/jira-software-cloud/docs/set-up-issue-types-in-team-managed-projects/">issue type</a>{' '}
              layout settings and confirm the estimate field is enabled.
            </li>
          </ul>
        </Callout>

        <ProseHeading level={3} id="app-home">App Home</ProseHeading>
        <p>
          App Home is your personal dashboard inside Slack. Click <strong>Groomba</strong> in your Slack
          sidebar (under Apps) and select the <strong>Home</strong> tab.
        </p>
        <DefList title="From here you can">
          <DefItem term="Create new team">Start a new Groomba team.</DefItem>
          <DefItem term="Groom Jira issues">Pick Jira issues to estimate with your team.</DefItem>
          <DefItem term="View all teams">See all the teams in your workspace.</DefItem>
          <DefItem term="Help">Get a list of available commands.</DefItem>
          <DefItem term="Guide">Open this page.</DefItem>
          <DefItem term="Contact">Send us feedback or report an issue.</DefItem>
        </DefList>
        <p>
          Below the buttons you'll see your <strong>voting streak</strong> and any <strong>open polls</strong>,
          including each issue's details, a link to the poll in Slack, and whether you've voted yet. Up to
          50 polls are listed, and the tab keeps itself current as votes come in and polls open and close,
          so you don't have to reopen it.
        </p>

        <SkFigure variant="home"><SlackAppHome iconSrc={iconSrc} /></SkFigure>

        <ProseHeading level={3} id="team-dashboard">Team dashboard</ProseHeading>
        <DocsNavPath command="@Groomba status" />
        <p>
          Mention <strong>@Groomba</strong> in a team channel and it replies in a thread with that team's
          dashboard: the issues polling right now, how much unrefined work is left in the backlog, which
          issues are deferred, and who takes part. Plain <strong>@Groomba</strong> on its own does the same.
        </p>
        <p>
          Slack limits how much one message can hold, so a busy team's dashboard lists only the first of
          each: up to 15 polling issues, 30 deferred issues, and 50 participants. It closes each list with
          a count of what it left out, like <em>... and 141 more</em>. Long issue titles are dropped before
          the issues themselves are, so a line may show a key with no title. The totals above each list are
          always the real ones, and <a href="#app-home">App Home</a> lists more of the polls you can vote on.
        </p>

        <hr />

        {/* ═══ ESTIMATING ═════════════════════════════════════ */}
        <ProseHeading level={2} id="estimating">Estimating</ProseHeading>

        <ProseHeading level={3} id="estimating-issues">Estimating Jira issues</ProseHeading>
        <DocsNavPath command="/groomba ISSUE-1 ISSUE-2" />
        <p>
          You can poll any Jira issue you have access to. Selected issues will start polling in your team
          channel using the settings configured in <code>/groomba settings</code>.
        </p>
        <p>
          You can also paste Jira URLs directly, e.g.{' '}
          <code>/groomba https://yoursite.atlassian.net/browse/ISSUE-1</code>. Groomba extracts the issue
          key from the URL automatically.
        </p>
        <p>
          If your workspace has the <strong>Jira Cloud</strong> Slack app installed, a pasted Jira link
          turns into a preview a second or so after you paste it, and Groomba can no longer find the
          issue. Press <code>Ctrl</code>&nbsp;+&nbsp;<code>Z</code>{' '}
          (<code>Cmd</code>&nbsp;+&nbsp;<code>Z</code> on a Mac) straight after pasting to turn it back
          into a plain URL. To stop it happening in a channel at all, run <code>/jira unfurl</code> there
          and choose <strong>Turn Off</strong>.
        </p>
        <p>
          Use <code>/groomba ISSUE-1</code> to bypass any backlog filters you've set up. You can poll{' '}
          <em>Bug</em> type issues even if bugs are explicitly disabled in your{' '}
          <strong>Backlog Settings</strong>.
        </p>

        <SkFigure variant="modal"><SlackGroomIssuesModal iconSrc={iconSrc} /></SkFigure>

        <ProseHeading level={3} id="voting">Voting</ProseHeading>
        <p>
          Each team member is either a <strong>participant</strong> (can vote) or an <strong>observer</strong>{' '}
          (sees polls but doesn't vote). You can change a member's status in your team settings.
        </p>
        <ol>
          <li>Each participant clicks a button to vote.</li>
          <li>The message updates with who has already voted.</li>
          <li>When every participant has voted (or the poll expires), the result is written back to Jira.</li>
        </ol>
        <DefList title="Possible poll results">
          <DefItem term="Estimate">An averaged point value is assigned in Jira.</DefItem>
          <DefItem term="Flag">Adds an impediment in Jira. The issue won't be auto-polled again until the flag is removed.</DefItem>
          <DefItem term="Defer">Skips the issue for now and re-queues it for polling after a configurable delay.</DefItem>
          <DefItem term="Done / Resolve">Transitions the issue to "Done" in Jira.</DefItem>
        </DefList>
        <p>
          Flag, Defer, and Resolve can each be enabled or disabled via{' '}
          <strong>Non-estimate voting options in polls</strong> in your team settings.
        </p>

        <SkFigure variant="channel" channel="# platform-core"><SlackVotingMessage iconSrc={iconSrc} /></SkFigure>

        <ProseHeading level={3} id="re-estimating">Re-estimating</ProseHeading>
        <p>After a poll wraps, you have a couple of follow-up options.</p>
        <DefList title="Follow-up actions">
          <DefItem term="Undo">Removes the resolution and comments in Jira.</DefItem>
          <DefItem term="Re-estimate">Removes the resolution + comments and creates a new poll for the same issue.</DefItem>
        </DefList>
        <p>
          You can also re-poll any issue by typing <code>/groomba ISSUE-1</code> or pasting a Jira URL.
        </p>

        <SkFigure variant="channel" channel="# platform-core" hasMenu><SlackPostVotingOptions iconSrc={iconSrc} /></SkFigure>

        <ProseHeading level={3} id="paused-polls">Paused polls</ProseHeading>
        <p>A poll pauses when its team can't run it, and picks up where it left off when the team is back.</p>
        <p>
          Polls pause when a team is disconnected from Jira, or disabled because its subscription was
          deactivated. A paused poll hides its vote buttons, keeps the votes already cast, and can't
          expire. Its message says why it's paused and what brings it back. You can still cancel it from
          the poll's menu.
        </p>
        <p>
          When the team is active again, each paused poll resumes with the time it had left, and at least
          an hour. A poll that stays paused for 30 days ends, the same as a cancelled poll.
        </p>
        <p>
          For example, if a team's Jira login stops working two hours before a poll closes, the poll
          pauses. When the owner runs <code>/groomba connect</code> the next morning, the poll comes back
          with two hours left and every vote still in place.
        </p>

        <hr />

        {/* ═══ BACKLOG & AUTO-POLLING ════════════════════════════ */}
        <ProseHeading level={2} id="backlog">Backlog &amp; Auto-polling</ProseHeading>

        <ProseHeading level={3} id="auto-polling">Auto-polling from backlog</ProseHeading>
        <p>
          If your Jira backlog has unrefined issues, estimate polls arrive in your team channel. Just
          click to vote.
        </p>
        <p>
          The <strong>Maximum issues open from auto-polling backlog</strong> setting controls how many polls
          can be open at once. The default is <strong>Intelligent</strong>, which picks that number for you
          from the size of your backlog: enough to keep a small backlog moving, without flooding the
          channel when it's large. You can also set a fixed value (1, 2, 3, 5, 8, 13, 21, or 99), or set
          it to <strong>0</strong> to turn auto-polling off entirely.
        </p>

        <SkFigure variant="modal"><SlackAutoPollSettings iconSrc={iconSrc} /></SkFigure>

        <ProseHeading level={3} id="backlog-settings">Backlog settings</ProseHeading>
        <DocsNavPath command="/groomba settings" steps={['Backlog settings']} />
        <p>The <strong>backlog</strong> is the source Groomba pulls unestimated issues from. Pick one of three sources:</p>
        <ul>
          <li>
            <strong>Jira board</strong> <span style={{color:'var(--gr-ink-400)', fontWeight:500}}>(recommended)</span>:{' '}
            issues are polled in your board's rank order, so the most important ones are estimated first.
          </li>
          <li><strong>Project</strong>: works, but rank ordering isn't preserved.</li>
          <li>
            <strong>Saved filter</strong>: defined by JQL, so you can target a specific sprint, epic, or
            any custom criteria.
          </li>
        </ul>
        <p>
          Select <strong>None</strong> to clear the backlog. You can still groom issues manually with{' '}
          <code>/groomba</code>, but auto-polling will be disabled.
        </p>
        <p>
          The <strong>estimate field</strong> determines which Jira field Groomba writes story points to.
          The default is "Story point estimate". See <a href="#creating-a-team">Creating a team</a> for
          the full list of options.
        </p>
        <Callout kind="note" title="Auto-polling scope.">
          <p>
            From a project or saved filter, Groomba auto-polls unestimated issues in Jira's{' '}
            <strong>To Do</strong>{' '}
            <a href="https://support.atlassian.com/jira-cloud-administration/docs/what-is-a-workflow-status/">status category</a>, including any custom statuses you've grouped
            under <strong>To Do</strong> (like{' '}
            <em>Open</em>, <em>Backlog</em>, or <em>Ready for Pointing</em>). A board instead uses
            the board's own scope, so what appears on your board and backlog is what Groomba polls.
            Either way, use <strong>Skip automatic polling for selected issue types</strong> to
            exclude types. Epic is excluded by default.
          </p>
        </Callout>

        <SkFigure variant="modal"><SlackBacklogSettings iconSrc={iconSrc} /></SkFigure>

        <ProseHeading level={3} id="turning-off-auto-polling">Turning off auto-polling</ProseHeading>
        <DocsNavPath command="/groomba settings" steps={['Auto-polling settings', 'Maximum issues open from auto-polling backlog']} value="0 (off)" />
        <p>
          Open team settings, then drop <strong>Maximum issues open from auto-polling backlog</strong> to{' '}
          <strong>0 (off)</strong>. Manual polls via <code>/groomba</code> are unaffected.
        </p>

        <hr />

        {/* ═══ TEAM SETTINGS ═════════════════════════════════════ */}
        <ProseHeading level={2} id="team-settings">Team settings</ProseHeading>

        <ProseHeading level={3} id="reminder-settings">Reminder settings</ProseHeading>
        <DocsNavPath command="/groomba settings" steps={['Reminder settings']} />
        <p>
          Reminders are direct messages from the bot to people who still owe an estimate. They are
          batched: one message covering every poll a participant hasn't voted on, never one message
          per poll. Two kinds are configured here, and they can run together.
        </p>

        <ProseHeading level={4}>Poll-tied reminders</ProseHeading>
        <p>Fire at most once each per poll, timed against that poll's own start and expiry.</p>
        <DefList>
          <DefItem term="Remind participants when new poll has started?">
            10 min after, 25 min after, 55 min after, or Off.
          </DefItem>
          <DefItem term="Remind participants when poll is about to expire?">
            30 min before, 1 hour before, or Off.
          </DefItem>
        </DefList>

        <ProseHeading level={4}>Recurring reminder</ProseHeading>
        <p>
          Runs on a schedule you set, like a calendar reminder, whenever the polls themselves happened
          to open. It repeats until the poll closes or the person votes. Pick a cadence, then fill in
          the fields it asks for.
        </p>
        <DefList title="Cadences">
          <DefItem term="Daily (weekdays)">Every Monday to Friday.</DefItem>
          <DefItem term="Weekly">Repeats on the days you pick under <strong>Which days?</strong></DefItem>
          <DefItem term="Every 2 weeks">
            <strong>Starting date</strong> sets both the first reminder and the weekday it repeats on.
          </DefItem>
          <DefItem term="Monthly">
            <strong>A date in the target week</strong> sets the weekday and which week of the month.
            Pick the 8th of a month that starts on Monday and you get the 2nd Tuesday.
          </DefItem>
        </DefList>
        <p>
          <strong>Time of day</strong> offers hourly slots from 4:00am to 7:00pm, in the timezone set
          under <strong>Auto-polling settings</strong>. Once the schedule is complete the modal reads it
          back to you in words, with the date and time of the next reminder.
        </p>

        <ProseHeading level={4}>What new teams start with</ProseHeading>
        <ul>
          <li>Recurring reminder: <em>Weekly</em>, on Mondays at 10:00am</li>
          <li>Both poll-tied reminders: <em>Off</em></li>
        </ul>
        <p>Teams created before the recurring reminder existed keep the settings they already had.</p>

        <SkFigure variant="modal"><SlackReminderSettings iconSrc={iconSrc} /></SkFigure>

        <ProseHeading level={3} id="averaging-settings">Averaging settings</ProseHeading>
        <DocsNavPath command="/groomba settings" steps={['Averaging settings']} />
        <p>Configure how Groomba combines your team's votes.</p>
        <DefList title="Averaging options">
          <DefItem term="Snap result to scale">Keep results on your pointing scale, or allow off-scale values.</DefItem>
          <DefItem term="Ceiling / floor / round">How fractional results are resolved.</DefItem>
          <DefItem term="Averaging method">Mean, median, geometric mean, or harmonic mean.</DefItem>
          <DefItem term="Rounding precision">Whole points, half points, or tenths (off-scale only).</DefItem>
          <DefItem term="Rounding cutoff">Custom threshold for rounding up, e.g. cutoff 0.2 means 4.2 rounds up to 5 (off-scale + round only).</DefItem>
          <DefItem term="Average on what value?">Point values or scale index (on-scale only).</DefItem>
        </DefList>
        <p>
          Use <em>Test your configuration</em> to preview results with example votes. The pre-rounded
          average is shown alongside for comparison.
        </p>

        <SkFigure variant="modal"><SlackAveragingSettings iconSrc={iconSrc} /></SkFigure>

        <ProseHeading level={3} id="auto-polling-settings">Auto-polling settings</ProseHeading>
        <DocsNavPath command="/groomba settings" steps={['Auto-polling settings']} />
        <p>
          When you create a team, Groomba picks smart defaults for how auto-polling runs. The{' '}
          <strong>Auto-polling settings</strong> modal lets you customize concurrency, pacing, rate limits,
          and schedule in one place.
        </p>
        <ProseHeading level={4}>Smart default working hours</ProseHeading>
        <ul>
          <li>Days of the week: Mon–Fri</li>
          <li>Times of day: 09:00 – 17:00</li>
          <li>Timezone: US Eastern. Change this first, since the rest is read against it</li>
        </ul>
        <p>
          These govern when auto-polling starts polls, and they stretch a poll's expiry so it doesn't
          land on a day you aren't working. Reminders are not held back by them: poll-tied reminders
          follow their poll, and the recurring reminder follows its own <strong>Time of day</strong>.
        </p>
        <ProseHeading level={4}>Configurable auto-poll rate limit</ProseHeading>
        <p>
          Team owners can optionally cap how many polls auto-polling starts within a time window. Useful for
          larger backlogs where you want to control the pace of estimation.
        </p>
        <DefList title="Rate limit options">
          <DefItem term="Max auto-polls per time window">Unlimited, 1, 3, 5, 10, 20, 50, 100, 200, or 500.</DefItem>
          <DefItem term="Time window">1 day, 3 days, 1 week, 2 weeks, or 1 month.</DefItem>
        </DefList>
        <p>
          New teams default to <strong>10 polls per day</strong>, but you can change this to{' '}
          <strong>Unlimited</strong> or any other value at any time, and there's no hard limit. Manual polls
          (started via <code>/groomba</code>) are never affected by this setting.
        </p>

        <SkFigure variant="modal"><SlackAutoPollSettings iconSrc={iconSrc} /></SkFigure>

        <ProseHeading level={3} id="poll-display-settings">Poll display settings</ProseHeading>
        <DocsNavPath command="/groomba settings" steps={['Poll display settings']} />
        <p>Customize how estimation polls appear in your Slack channel.</p>
        <DefList title="Display options">
          <DefItem term="Issue descriptions">Show Jira issue descriptions inline.</DefItem>
          <DefItem term={'"Cast your vote" header'}>Show a header above the voting buttons.</DefItem>
          <DefItem term="Running result">Preview the current result while polling is active.</DefItem>
          <DefItem term="Vote results">Show individual vote values (who voted what).</DefItem>
          <DefItem term="Awaiting voters">Show which participants haven't voted yet.</DefItem>
          <DefItem term="Poll requester">Add a "Sent by" line naming whoever queued the issue.</DefItem>
        </DefList>
        <p>
          You can also choose whether vote buttons appear in a single row or split across multiple rows.
          The live preview updates as you change settings.
        </p>

        <SkFigure variant="modal"><SlackPollDisplaySettings iconSrc={iconSrc} /></SkFigure>

        <ProseHeading level={3} id="jira-update-settings">Jira update settings</ProseHeading>
        <DocsNavPath command="/groomba settings" steps={['Jira update settings']} />
        <p>
          Control what Groomba writes back to the Jira issue as a poll opens and finishes.
        </p>
        <DefList title="Jira commenting">
          <DefItem term="Comment when discussion occurs">
            Posts a comment only when participants have talked in the poll thread.
          </DefItem>
          <DefItem term="Always comment">Posts a link to the poll on every estimated issue.</DefItem>
          <DefItem term="Off">Writes no comments.</DefItem>
          <DefItem term="Include voting results">
            Adds each person's vote to the comment, not just the average.
          </DefItem>
        </DefList>

        <p>
          <strong>Jira issue transitions</strong> move the issue's status for you, so anyone looking at the
          board can see what has already gone out to a poll. Pick a status for either moment, or leave it
          blank.
        </p>
        <DefList title="Transitions">
          <DefItem term="When a poll opens">
            Moves the issue to the status you pick as soon as it starts taking votes, for example from
            "Ready for pointing" to "Sent to pointing".
          </DefItem>
          <DefItem term="When points are assigned">
            Moves the issue to the status you pick once the team has agreed on an estimate and Groomba has
            written it, for example from "Sent to pointing" to "Estimated".
          </DefItem>
        </DefList>
        <p>
          Groomba never moves an issue somebody has already started. "When a poll opens" only moves an
          issue in the <strong>To Do</strong>{' '}
          <a href="https://support.atlassian.com/jira-cloud-administration/docs/what-is-a-workflow-status/">status category</a>. "When points are assigned" only moves an issue still
          sitting in the status the poll opened it into.
        </p>
        <p>
          So if an issue goes out to a poll and someone picks it up and moves it to <em>In Progress</em>{' '}
          while voting is on, Groomba leaves it there. The poll still finishes and the points are still
          written, the status just stays put. An issue that was already in progress when its poll started
          is left alone the same way.
        </p>
        <p>
          If you leave "When a poll opens" blank, "When points are assigned" moves any issue in the{' '}
          <strong>To Do</strong> status category.
        </p>
        <p>
          If a poll is undone, cancelled or re-estimated, Groomba moves the issue back out of the statuses
          it set.
        </p>
        <p>
          Statuses come from the workflows in your backlog's space, and Jira has the final say: when a
          workflow will not allow a move, the issue stays where it is and the poll says so.
        </p>

        <hr />

        {/* ═══ ADMINISTRATION ════════════════════════════════════ */}
        <ProseHeading level={2} id="administration">Administration</ProseHeading>

        <ProseHeading level={3} id="team-ownership">Updating the owner of a team</ProseHeading>
        <DocsNavPath command="/groomba settings" steps={['Ownership settings']} />
        <p>
          Each team has a <strong>primary owner</strong> and optional <strong>secondary owners</strong>. The
          primary owner's Jira credentials are used for all API calls on that team.
        </p>
        <DefList title="Owner roles">
          <DefItem term="Primary owner">Full control over the team. Can transfer ownership to another user.</DefItem>
          <DefItem term="Secondary owners">Can modify team settings but cannot transfer primary ownership.</DefItem>
        </DefList>
        <p>
          If the primary owner leaves the Slack workspace, Groomba automatically transfers ownership to the
          best available team member.
        </p>

        <SkFigure variant="modal"><SlackOwnershipSettings iconSrc={iconSrc} /></SkFigure>

        <ProseHeading level={3} id="workspace-admin">Updating workspace admin</ProseHeading>
        <DocsNavPath command="/groomba workspace" steps={['Workspace settings']} />
        <p>
          Workspace admins manage your organization's Groomba install. There's one{' '}
          <strong>primary admin</strong> (whoever installed Groomba, by default) and optional{' '}
          <strong>secondary admins</strong>.
        </p>
        <DefList title="Admin roles">
          <DefItem term="Primary admin">Can manage all teams and transfer the workspace admin role.</DefItem>
          <DefItem term="Secondary admins">Can see and manage all teams from the web portal and from <code>/groomba workspace</code>.</DefItem>
        </DefList>
        <p>
          Run <code>/groomba workspace</code> from anywhere in Slack to see your role, every team in
          the workspace, and a shortcut into <strong>Workspace settings</strong>.
        </p>

        <SkFigure variant="modal"><SlackWorkspaceListing iconSrc={iconSrc} /></SkFigure>

        <p>
          Click <strong>Workspace settings</strong> to manage admins or transfer primary ownership. If the
          primary admin leaves the Slack workspace, Groomba automatically promotes a successor.
        </p>
        <p>
          If a workspace has no primary admin at all, <code>/groomba workspace</code> says so instead of
          naming one. Add one by installing Groomba again: whoever clicks <strong>Add to Slack</strong>
          takes the role.
        </p>

        <SkFigure variant="modal"><SlackWorkspaceSettings iconSrc={iconSrc} /></SkFigure>

        <ProseHeading level={3} id="web-portal">Using the web portal</ProseHeading>
        <DocsNavPath command="/groomba portal" />
        <p>
          The web portal is a dashboard view of your Groomba teams outside Slack. Type{' '}
          <code>/groomba portal</code> in Slack and click the link to open it.
        </p>
        <p>From the portal you can:</p>
        <ul>
          <li>See each team's name, channel, owner, Jira connection status, and team status</li>
          <li>Open a team's Slack channel directly</li>
          <li>Delete teams you own or manage</li>
        </ul>
        <p>
          Workspace admins see all teams; regular users see only their own. Team settings are still managed
          in Slack via <code>/groomba settings</code>.
        </p>
        <ProseHeading level={4}>Usage</ProseHeading>
        <p>
          The <strong>Usage</strong> tab is for workspace admins only, since it shows spend. It covers the
          last month, which is the same window your usage is reported on. The figures are measured once a
          day, so a brand new workspace sees its first figures the next day.
        </p>
        <DefList title="What the figures mean">
          <DefItem term="Estimates cast">Votes on a poll. Deferring, flagging and resolving don't count.</DefItem>
          <DefItem term="People estimating">Counted once each, however many teams they estimated on.</DefItem>
          <DefItem term="Billed seats">
            What your usage is reported as, not a headcount. See{' '}
            <a href="#seat-counting">How seats are counted</a>.
          </DefItem>
        </DefList>
        <p>
          Below the totals, each team is listed busiest first with its members, how many of them estimated,
          and how many estimates they cast.
        </p>

        <SkFigure variant="channel" channel="Direct message · Groomba"><SlackPortalMessage iconSrc={iconSrc} /></SkFigure>

        <ProseHeading level={3} id="managing-usage">Managing Groomba usage</ProseHeading>
        <p>
          <strong>Your organization's teams.</strong> The fastest way to see every team in your workspace
          is to run <code>/groomba workspace</code> from anywhere in Slack. You'll get the listing shown
          in <a href="#workspace-admin">Updating workspace admin</a> above.
        </p>
        <p>
          <strong>Deleting teams.</strong> Open the team's channel and run <code>/groomba settings</code>{' '}
          there.
        </p>

        <hr />

        {/* ═══ PERMISSIONS ══════════════════════════════════════ */}
        <ProseHeading level={2} id="permissions">Permissions</ProseHeading>

        <ProseHeading level={3} id="permissions-overview">What Groomba can and can't see</ProseHeading>
        <p>
          Groomba uses standard OAuth scopes for both Slack and Jira. Every scope below maps to a
          specific Groomba feature, and Groomba does not read, store, or transmit data outside what
          those features need. You can revoke access at any time from your Slack workspace's Apps
          directory or your{' '}
          <a href="https://id.atlassian.com/manage-profile/apps">Atlassian Connected apps</a> page;
          revocation is effective immediately on the upstream side.
        </p>

        <ProseHeading level={3} id="slack-permissions">Slack permissions</ProseHeading>
        <p>
          Granted by the Slack workspace admin who installs Groomba. The scopes apply to the
          Groomba bot user, created at install time, which performs every action listed below.
        </p>
        <DefList title="Slack scopes Groomba requests">
          <DefItem term="commands">
            Slash commands. Powers <code>/groomba</code> and its variants throughout this guide.
          </DefItem>
          <DefItem term="app_mentions:read">
            Receive events when Groomba is @-mentioned, so you can talk to Groomba by typing{' '}
            <strong>@Groomba help</strong> in a channel.
          </DefItem>
          <DefItem term="chat:write">
            Send messages as the Groomba bot. Used for every message Groomba posts: polls,
            vote-result summaries, reminders, and ephemeral help responses.
          </DefItem>
          <DefItem term="chat:write.public">
            Post messages in public channels Groomba isn't a member of. Used so commands invoked
            from such a channel still produce a visible response.
          </DefItem>
          <DefItem term="channels:join">
            Let the bot join a public channel. Used when you pick a public channel as your team
            channel so Groomba can post polls there.
          </DefItem>
          <DefItem term="channels:manage">
            Set the topic and purpose of a public team channel during setup. Groomba does not use
            the broader channel-management actions this scope can grant (create, archive, rename).
          </DefItem>
          <DefItem term="groups:write">
            Create a new private channel and set its topic and purpose. Used by the "Create a new
            private channel for this team" option during setup.
          </DefItem>
          <DefItem term="channels:read">
            List public channels and read their metadata. Powers the team-channel picker, and lets
            Groomba react to channel-archived or channel-renamed events so a team's stored channel
            reference stays current.
          </DefItem>
          <DefItem term="groups:read">
            Same as <code>channels:read</code>, but for private channels Groomba has been added to.
          </DefItem>
          <DefItem term="users:read">
            Read the workspace user list. Used to build participant pickers, render member names
            in polls, and react to <code>user_change</code> events when a member is deactivated.
          </DefItem>
          <DefItem term="users:read.email">
            Read the email on Slack user profiles. Used at Jira-connect time to match each Slack
            participant to their Jira account by email. Not used for marketing.
          </DefItem>
          <DefItem term="channels:history, groups:history">
            Read the thread of a poll Groomba has posted, so the Jira comment Groomba writes back
            after a vote can include reply context. Also delivers <code>message</code> events so
            users can invoke Groomba by plain text in channels Groomba is in. Limited by Slack to
            channels the bot has been added to.
          </DefItem>
          <DefItem term="im:read, im:history">
            Open a direct-message channel with a user (for poll reminders) and receive DMs sent to
            the Groomba bot. Groomba does not see DMs between other workspace members.
          </DefItem>
          <DefItem term="mpim:read, mpim:history">
            Same as <code>im:*</code>, but for multi-person DMs that include the Groomba bot.
            Limited to MPIMs Groomba is a participant of.
          </DefItem>
        </DefList>
        <p>
          Separately, signing in to the <a href="#web-portal">web portal</a> uses{' '}
          <code>openid</code>, <code>email</code> and <code>profile</code>. These are granted by you
          when you sign in, not by the workspace admin at install, and only identify you to the
          portal.
        </p>
        <Callout kind="note" title="What Groomba does not see.">
          <p>
            Groomba does not read messages in channels it hasn't been invited to, and does not
            receive DMs that don't include the Groomba bot. The history scopes are restricted by
            Slack itself to channels where the bot is a member.
          </p>
        </Callout>

        <ProseHeading level={3} id="jira-permissions">Jira permissions</ProseHeading>
        <p>
          Granted by the Jira user who connects Groomba on behalf of a team. That user's
          credentials are used for the team's Jira reads and writes; Groomba does not maintain a
          shared service account in your Jira instance.
        </p>
        <DefList title="Atlassian OAuth 2.0 (3LO) scopes Groomba requests">
          <DefItem term="offline_access">
            Lets Groomba refresh its short-lived (~1 hour) Jira access token automatically. Without
            it, you would have to re-authorize Groomba every hour.
          </DefItem>
          <DefItem term="read:jira-user">
            Read Jira user information. Used to look up the connected user and match Slack
            participants to Jira accounts by email.
          </DefItem>
          <DefItem term="read:jira-work">
            Read Jira issues, comments, transitions, custom fields, and screen configuration. The
            core read scope; powers backlog fetch, issue rendering in polls, the numeric-field
            picker for your story-point destination, and the transition list when resolving issues.
          </DefItem>
          <DefItem term="write:jira-work">
            Edit Jira issues. Used to write story points to the configured estimate field, post the
            vote-summary comment, transition issues to "Done" on resolve votes, and toggle the
            Flagged field.
          </DefItem>
          <DefItem term="manage:jira-project">
            Check project-level permissions before writes. Used by Groomba's pre-flight check so it
            can return a clear error if your Jira user lacks permission to update an issue, rather
            than failing with a 403 mid-poll.
          </DefItem>
          <DefItem term="manage:jira-configuration">
            Read Jira screen configuration and, when the connected Jira user has Jira admin
            permission, add a missing estimate or Flagged field to the issue's edit screen during
            poll write-back. Without admin permission, Groomba instead posts a warning asking you
            to add the field manually.
          </DefItem>
          <DefItem term="read:board-scope:jira-software">
            View Jira boards and backlogs. Required to use a Jira board as your backlog source, so
            Groomba can read the board's rank order and auto-poll the highest-priority issues
            first.
          </DefItem>
          <DefItem term="read:project:jira">
            View Jira projects. Required by Atlassian for the <code>GET /board</code> endpoint
            Groomba uses to list available boards in team settings.
          </DefItem>
          <DefItem term="read:issue-details:jira">
            View Jira issue details. Required by Atlassian for the{' '}
            <code>GET /board/{'{id}'}/backlog</code> endpoint Groomba uses to pull issues from your
            selected board.
          </DefItem>
        </DefList>
        <Callout kind="note" title="Revoking access.">
          <p>
            Slack admins can revoke Groomba from their workspace's Apps directory at any time.
            Jira users can revoke Groomba from{' '}
            <a href="https://id.atlassian.com/manage-profile/apps">Atlassian &gt; Connected apps</a>.
            Revocation severs Groomba's access immediately on the upstream side; Groomba receives
            the corresponding events and cleans up local team state.
          </p>
        </Callout>

        <hr />

        {/* ═══ PRICING & BILLING ═════════════════════════════════ */}
        <ProseHeading level={2} id="pricing">Pricing &amp; billing</ProseHeading>

        <ProseHeading level={3} id="subscription">Subscription</ProseHeading>
        <DocsNavPath command="/groomba subscription" />
        <p>
          We deeply appreciate your support. It keeps the lights on and lets us keep building features
          that save developer time.
        </p>
        <p>
          Any member of your Slack workspace can run <code>/groomba subscription</code> to create or manage
          the subscription through Stripe's portal. We can also make manual changes if you contact us via{' '}
          <code>/groomba feedback</code> or <a href="mailto:hello@groomba.ai">hello@groomba.ai</a>.
        </p>

        <ProseHeading level={3} id="pricing-tiers">Pricing tiers</ProseHeading>
        <DefList title="Plans">
          <DefItem term="Free">Your first team is free with full functionality. Perfect for trying Groomba with a single team.</DefItem>
          <DefItem term="Standard">$4 / mo per seat. For organizations using multiple teams. Billed on the unique seats used during each billing period.</DefItem>
          <DefItem term="Enterprise">Custom pricing for large organizations. Email <a href="mailto:hello@groomba.ai">hello@groomba.ai</a>.</DefItem>
        </DefList>

        <ProseHeading level={3} id="seat-counting">How seats are counted</ProseHeading>
        <p>Your seat count determines your bill. Here's how it's computed.</p>
        <DefList title="Seat rules">
          <DefItem term="Who counts">Any unique Slack user who is a team participant or who voted in a poll during the billing period. The same person across multiple teams counts once. Team owners aren't counted unless they also participate or vote.</DefItem>
          <DefItem term="One team free">Members of your <em>smallest</em> active team who are on no other team aren't counted, so running a single team costs nothing.</DefItem>
          <DefItem term="Duplicate teams">A team whose members all appear on other teams adds no seats. From the third identical team onward, each one adds <strong>1 seat</strong>, so the free team can't be multiplied.</DefItem>
        </DefList>
        <Callout kind="note" title="Worked example.">
          <p>
            Workspace with Team A (Alice, Bob, Carol) and Team B (Bob, Dave). Bob is deduplicated, so 4
            unique people. Team B is the smaller team, and Dave is its only member who is on no other
            team, so Dave is subtracted, leaving <strong>3 billable seats</strong>.
          </p>
        </Callout>

        <hr />

        {/* ═══ SUPPORT ═════════════════════════════════════════ */}
        <ProseHeading level={2} id="support">Support</ProseHeading>

        <ProseHeading level={3} id="feedback">Submitting feedback</ProseHeading>
        <DocsNavPath command="/groomba feedback" />
        <p>
          You can reach us with <code>/groomba feedback</code> in any channel, or by emailing{' '}
          <a href="mailto:hello@groomba.ai">hello@groomba.ai</a>.
        </p>

        <SkFigure variant="modal"><SlackFeedbackModal iconSrc={iconSrc} /></SkFigure>
      </Prose>

      <DocsFooter updated="April 2026" version="3.6" />
    </article>
  );
}
